import { Component, ViewChild, ViewContainerRef } from "@angular/core";
import { FieldType, FormlyFieldConfig } from "@ngx-formly/core";
import { GridEventService } from "../../../services/grid.service";
import { parseCellValue } from '../../../shared/utils/helpers';

@Component({
    standalone: false,
    selector: 'app-grid-summary',
    templateUrl: './grid-summary.component.html',
    styleUrls: ['./grid-summary.component.css']
})
export class FormlyGridSummary extends FieldType {
    @ViewChild("fieldComponent", { read: ViewContainerRef })
    fieldComponent: ViewContainerRef;

    private selectionSet: Set<string> = new Set();

    gridFields: any;
    gridSummaryLabel: string = '';
    overallMaxScore: number = 0;
    overallTotalScore: number = 0;
    ratingLabel: string = '';
    ratingPercentage: number = 0;
    overallComments: string = '';
    displayTextboxColumns: { [key: string]: boolean } = {};
    hasTextboxColumns: boolean = false;

    constructor(private eventService: GridEventService) {
        super();
    }

    ngOnInit() {
        this.gridSummaryLabel = (this.field.templateOptions || {})?.label || '';

        this.setGridFields();
        this.hasTextboxColumns = this.hasAnyTextboxColumns();

        if (this.hasTextboxColumns) {
            this.overallMaxScore = (this.model[this.key as string] || {})?.maxScore || 0;
        }
        this.overallComments = (this.model[this.key as string] || {})?.overallComments || '';
        this.calculateScores(true);
        this.eventService.calculateScores$.subscribe(() => {
            this.calculateScores();
        });

    }

    setOverallCommentModel(value: string) {
        this.overallComments = value;
        (this.model[this.key as string] || {}).overallComments = value;
    }

    getGridMaxScore(gridField): number {
        let maxScore = 0;
        const { headers, rows } = gridField?.templateOptions ?? {};
        if (!headers?.length || !rows?.length) return 0;

        headers.forEach((header: any, headerIndex: number) => {
            if (!header.displaySumOfColumn) return;

            if (Array.isArray(rows)) {
                let hasNumber = false;
                let hasDropdown = false;
                const firstRow = rows[0];
                const column = firstRow?.columns?.[headerIndex];
                for (const row of rows) {
                    const column = row?.columns?.[headerIndex];
                    if (column?.type === "Number") hasNumber = true;
                    if (column?.type === "Dropdown") hasDropdown = true;
                }
                if (hasNumber && hasDropdown) {
                    maxScore += Number(this.model[gridField.key]?.maxScore) || 0;
                    return;
                } else if (column?.type === "Number" && this.displayTextboxColumns[`${gridField.key}_${headerIndex}`]) {
                    maxScore += Number(this.model[gridField.key]?.maxScore) || 0;
                } else {
                    rows.forEach((row: any, rowIndex: number) => {
                        const column = row?.columns?.[headerIndex];

                        // Only calculate maxScore for rows where tickbox is ticked
                        let tickboxIndex = row.columns?.findIndex((col: any) => col?.type === "Tickbox");
                        let tickboxValue = true;
                        if (tickboxIndex == undefined || tickboxIndex >= 0) {
                            const cell = ((this.model[gridField.key] || {}).cells || [])
                                .find((x: any) => x.rowIndex === rowIndex && x.columnIndex === tickboxIndex);
                            tickboxValue = !!(cell && parseCellValue(cell.value));
                            if (cell == undefined) {
                                tickboxValue = true;
                            }
                        }
                        if (this.canScore(gridField, rowIndex) && column && tickboxValue) {
                            switch (column.type) {
                                case "Dropdown":
                                case "Tickbox":
                                    maxScore += Math.max(...(column.options?.map(option => Number(option.value)) ?? [0]));
                                    break;
                            }
                        }
                    });
                }
            }
        });

        if (!this.model[gridField.key]) {
            this.model[gridField.key] = {};
        }
        this.model[gridField.key].maxScore = maxScore;
        return maxScore;
    }

    getGridTotalScore(gridField: any): number {
        let totalScore = 0;
        const { headers, rows } = gridField?.templateOptions ?? {};

        if (!headers?.length || !rows?.length) return 0;

        headers.forEach((header: any, headerIndex: number) => {
            if (!header.displaySumOfColumn) return;

            rows.forEach((row: any, rowIndex: number) => {
                if (this.canScore(gridField, rowIndex)) {
                    totalScore += Number(((this.model[gridField.key] || {}).cells || [])
                        .find(x => x.rowIndex == rowIndex && x.columnIndex == headerIndex)?.value) || 0;
                }
            });
        });

        if (!this.model[gridField.key]) {
            this.model[gridField.key] = {};
        }
        this.model[gridField.key].totalScore = totalScore;
        return totalScore;
    }

    setGridMaxScore(gridField: any, value: number) {
        if (this.model[gridField.key]) {
            this.model[gridField.key].maxScore = value;
            this.getGridMaxScore(gridField);
            this.getGridTotalScore(gridField);
            this.calculateScores(true);
        }
    }

    isTextFieldWithDisplayTotal(gridField: FormlyFieldConfig): boolean {
        const { headers, rows } = gridField?.templateOptions ?? {};

        if (!Array.isArray(headers) || !Array.isArray(rows) || !headers.length || !rows.length) return false;

        return headers.some((header: any, headerIndex: number) => {
            if (!header.displaySumOfColumn) return false;

            // const firstRow = rows[0];
            // const column = firstRow?.columns?.[headerIndex];

            for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
                const column = rows[rowIndex]?.columns?.[headerIndex];
                if (column?.type === "Number") {
                    return this.displayTextboxColumns[`${gridField.key}_${headerIndex}`];
                }
            }
        });
    }

    private setGridFields() {
        // Walk up to the form root, then scan the entire root.fieldGroup for grid fields.
        let root = this.field;
        while (root.parent) {
            root = root.parent;
        }

        const rootFieldGroup = (root.fieldGroup ?? []) as FormlyFieldConfig[];
        // If the form uses pages (wrappers), we still want to collect grids inside pages as well,
        // findGridFieldsRecursively handles nested fieldGroups.
        this.gridFields = this.findGridFieldsRecursively(rootFieldGroup);
    }

    private findGridFieldsRecursively(fieldGroup: FormlyFieldConfig[]): FormlyFieldConfig[] {
        let gridFields: FormlyFieldConfig[] = [];

        for (const field of fieldGroup) {
            if (
                typeof field.type === 'string' &&
                field.type.endsWith('grid') &&
                field.templateOptions?.headers?.some((header: any) => header.displaySumOfColumn) && (!field.templateOptions.hidden && !field.templateOptions.isParentLogicallyHidden)
            ) {
                gridFields.push(field);
            }

            if (field.fieldGroup && field.fieldGroup.length > 0) {
                gridFields = gridFields.concat(this.findGridFieldsRecursively(field.fieldGroup));
            }
        }
        return gridFields;
    }

    private calculateScores(onLoad: boolean = false): void {
        this.setGridFields();
        this.resetScores();
        this.setSelectionMap();
        this.gridFields.forEach(gridField => {
            this.overallMaxScore += this.getGridMaxScore(gridField);
            this.overallTotalScore += this.getGridTotalScore(gridField);
        });

        this.updateRatingInfo();
        this.updateModel();
        //if (!onLoad) {
        //    this.updateModel();
        //}
    }

    private resetScores(): void {
        this.overallTotalScore = 0;
        this.overallMaxScore = 0;
    }

    private updateRatingInfo(): void {
        this.ratingPercentage = this.calculateRatingPercentage();
        this.ratingLabel = this.getRatingLabel();
    }

    private calculateRatingPercentage(): number {
        const overallMaxScore = this.overallMaxScore || 1;
        const percentage = (this.overallTotalScore / overallMaxScore) * 100;
        return isNaN(percentage) ? 0 : Math.round(percentage);
    }

    private getRatingLabel(): string {
        const ratings: Rating[] = this.field.templateOptions?.ratings ?? [];
        return ratings.find(rating =>
            this.ratingPercentage >= rating.min &&
            this.ratingPercentage <= rating.max
        )?.label ?? '';
    }

    private updateModel(): void {
        this.model[this.key as string] = {
            totalScore: this.overallTotalScore,
            maxScore: this.overallMaxScore,
            ratingPercentage: this.ratingPercentage,
            ratingLabel: this.ratingLabel,
            overallComments: this.overallComments
        };
    }

    private setSelectionMap(): void {
        this.selectionSet.clear();

        for (const gridField of this.gridFields) {
            const rows = gridField?.templateOptions?.rows || [];
            for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
                const row = rows[rowIndex];
                const tickboxColumnIndex = row.columns?.findIndex(
                    (column: any) => column?.type === "Tickbox"
                );
                if (tickboxColumnIndex !== undefined && tickboxColumnIndex >= 0) {
                    const cell = ((this.model[gridField.key] || {}).cells || [])
                        .find((x: any) => x.rowIndex === rowIndex && x.columnIndex === tickboxColumnIndex);
                    if ((cell && parseCellValue(cell.value)) || !cell) {
                        this.selectionSet.add(`${gridField.key}_${rowIndex}`);
                    }
                }
            }
        }
    }

    private isScoreEnabled(gridField: any, rowIndex: number) {
        const key = `${gridField.key}_${rowIndex}`;
        return this.selectionSet.has(key);
    }

    private canScore(gridField: any, rowIndex: number): boolean {
        const hasEnabler = Array.from(this.selectionSet).some(entry => entry.startsWith(`${gridField.key}_`));
        return !hasEnabler || this.isScoreEnabled(gridField, rowIndex);
    }

    private hasAnyTextboxColumns(): boolean {
        //const gridFields = this.field.parent?.fieldGroup?.filter(field => typeof field.type === 'string' && field.type.endsWith('grid')) || [];

        for (const gridField of this.gridFields) {
            const headers = gridField.templateOptions?.headers || [];
            const rows = gridField.templateOptions?.rows || [];
            for (let headerIndex = 0; headerIndex < headers.length; headerIndex++) {
                if (!headers[headerIndex].displaySumOfColumn) continue;
                for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
                    const column = rows[rowIndex]?.columns?.[headerIndex];
                    if (column?.type === "Number") {
                        this.displayTextboxColumns[`${gridField.key}_${headerIndex}`] = true;
                        // return true;
                    }
                }
            }
        }



        return false;
    }
}

interface Rating {
    min: number;
    max: number;
    label: string;
}
